import { type FC, useMemo } from 'react';
import { Empty, Spin } from 'antd';

import { useTpoGenConfig, useCustomerConfig } from '../../context/CustomerConfigContext';
import {
    SCENARIO_BASE_COLUMN_KEY,
    TPO_SCENARIO_COMPARISON_GRID,
    tpoScenarioKpiRowTestId,
    tpoTableColTestId,
} from '../../config/testIds';
import ScenarioColumn from './components/ScenarioColumn';
import EmptyScenarioSlot from './components/EmptyScenarioSlot';
import type { ScenarioPlan } from '../types';

interface ScenarioComparisonGridProps {
    baseScenario: ScenarioPlan | null;
    comparisonScenarios: ScenarioPlan[];
    selectedKpiKeys: string[];
    /** KPI keys returned by the search-filtered API; `null` means no search is applied. */
    matchedKpiKeys?: string[] | null;
    kpiByPlanId: Map<string, Record<string, number | string | null>>;
    deltaByPlanId: Map<string, Record<string, number | null>>;
    isKpiLoading?: boolean;
    highlightedKpiKey?: string | null;
    onHighlightChange?: (kpiKey: string | null) => void;
    onRemoveScenario?: (planId: string) => void;
    onAddScenarioClick?: () => void;
    showPercent?: boolean;
}

const EMPTY_KPI: Record<string, number | string | null> = {};
const EMPTY_DELTA: Record<string, number | null> = {};

const ScenarioComparisonGrid: FC<ScenarioComparisonGridProps> = ({
    baseScenario,
    comparisonScenarios,
    selectedKpiKeys,
    matchedKpiKeys,
    kpiByPlanId,
    deltaByPlanId,
    isKpiLoading,
    highlightedKpiKey,
    onHighlightChange,
    onRemoveScenario,
    onAddScenarioClick,
    showPercent,
}) => {
    const { scenarioComparisonConfig, labels } = useTpoGenConfig();
    const { maxComparisonSlots, gridLayout } = scenarioComparisonConfig;
    const { kpiCatalog, metricFormatByKey } = useCustomerConfig();

    const selectedKpiSet = useMemo(() => new Set(selectedKpiKeys), [selectedKpiKeys]);

    const matchedKpiSet = useMemo(() => (matchedKpiKeys ? new Set(matchedKpiKeys) : null), [matchedKpiKeys]);

    const visibleKpis = useMemo(
        () => kpiCatalog.filter((kpi) => selectedKpiSet.has(kpi.key) && (!matchedKpiSet || matchedKpiSet.has(kpi.key))),
        [kpiCatalog, selectedKpiSet, matchedKpiSet],
    );

    const emptySlotCount = Math.max(0, maxComparisonSlots - comparisonScenarios.length);

    const baseKpiValues = baseScenario ? kpiByPlanId.get(baseScenario.planId) ?? EMPTY_KPI : EMPTY_KPI;

    return (
        <Spin spinning={!!isKpiLoading} size="small">
            <div
                className="overflow-auto"
                style={{ maxHeight: gridLayout.gridMaxHeight }}
                data-testid={TPO_SCENARIO_COMPARISON_GRID}
            >
                {visibleKpis.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={labels.common.noData} />
                ) : (
                    <div className="flex gap-6 min-w-max">
                        <div
                            className="flex flex-col shrink-0 sticky left-0 bg-white z-20 border border-[#F0F0F0] rounded-lg"
                            style={{ width: gridLayout.kpiColumnWidth }}
                        >
                            <div
                                className="flex items-center px-3 bg-[#FAFAFA] font-medium text-sm rounded-t-lg border-b border-[#F0F0F0] sticky top-0 z-30"
                                style={{ height: gridLayout.headerHeight }}
                                data-testid={tpoTableColTestId('scenario_comparison', 'kpi')}
                            >
                                {labels.comparisonReport.kpiColumnHeader}
                            </div>
                            {visibleKpis.map((kpi) => (
                                <div
                                    key={kpi.key}
                                    className={`flex items-center px-3 border-b border-[#F0F0F0] text-sm cursor-pointer select-none last:border-b-0 last:rounded-b-lg ${
                                        kpi.key === highlightedKpiKey
                                            ? 'bg-[#F5F3FF] font-medium'
                                            : 'hover:bg-[#FAFAFA]'
                                    }`}
                                    style={{ height: gridLayout.rowHeight }}
                                    onClick={() => onHighlightChange?.(kpi.key === highlightedKpiKey ? null : kpi.key)}
                                    data-testid={tpoScenarioKpiRowTestId(kpi.key)}
                                    data-state-highlighted={kpi.key === highlightedKpiKey ? 'true' : undefined}
                                >
                                    {kpi.name}
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-1 gap-6 min-w-0">
                            <ScenarioColumn
                                planName={baseScenario?.planName ?? labels.comparisonReport.baseScenarioPlaceholder}
                                isBase
                                basePrefix={labels.comparisonReport.basePrefix}
                                kpis={visibleKpis}
                                kpiValues={baseKpiValues}
                                formatByKpiKey={metricFormatByKey}
                                highlightedKpiKey={highlightedKpiKey}
                                onHighlightChange={onHighlightChange}
                                showPercent={showPercent}
                                headerHeight={gridLayout.headerHeight}
                                rowHeight={gridLayout.rowHeight}
                                minWidth={gridLayout.scenarioColumnMinWidth}
                                deltaColors={scenarioComparisonConfig.deltaColors}
                                stickyHeader
                                columnKey={SCENARIO_BASE_COLUMN_KEY}
                            />

                            {comparisonScenarios.map((scenario) => (
                                <ScenarioColumn
                                    key={scenario.planId}
                                    planName={scenario.planName}
                                    kpis={visibleKpis}
                                    kpiValues={kpiByPlanId.get(scenario.planId) ?? EMPTY_KPI}
                                    kpiDeltas={deltaByPlanId.get(scenario.planId) ?? EMPTY_DELTA}
                                    formatByKpiKey={metricFormatByKey}
                                    highlightedKpiKey={highlightedKpiKey}
                                    onHighlightChange={onHighlightChange}
                                    onRemove={onRemoveScenario ? () => onRemoveScenario(scenario.planId) : undefined}
                                    showPercent={showPercent}
                                    headerHeight={gridLayout.headerHeight}
                                    rowHeight={gridLayout.rowHeight}
                                    minWidth={gridLayout.scenarioColumnMinWidth}
                                    deltaColors={scenarioComparisonConfig.deltaColors}
                                    stickyHeader
                                    columnKey={scenario.planId}
                                />
                            ))}

                            {Array.from({ length: emptySlotCount }, (_, i) => (
                                <EmptyScenarioSlot
                                    key={`empty-${i}`}
                                    onClick={baseScenario ? onAddScenarioClick : undefined}
                                    kpiCount={visibleKpis.length}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Spin>
    );
};

ScenarioComparisonGrid.displayName = 'ScenarioComparisonGrid';
export default ScenarioComparisonGrid;
